import { uniqBy } from 'lodash';
import moment from 'moment-timezone';
import {
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { useIntl } from 'react-intl';
import { AssistantChat, ForecastModal } from 'sonata-platform-components';
import {
  BackgroundSpinner,
  ButtonUI,
  ContextualMenu,
  EmptyState,
  IconTooltip,
  PacingStatus,
  Status,
  showNotification,
  Tag,
  Toast,
  Tooltip,
  TooltipInteractionEnum,
  useFeatureUser
} from 'sonata-react-components';
import { getEntityDiagnostic, getLineItemStatus } from 'sonata-static-storage/lib/entities';
import {
  abbreviateNumber,
  normalize,
  normalizeWithUnit,
  normalizeWithUnitAndThousandAndMillions
} from 'sonata-static-storage/lib/helpers/i18nUnits.helpers';

import { ChangelogModal } from '@/components/common/ChangelogModal';
import { PacingStatusModal } from '@/components/common/PacingStatusModal';
import { getPricingModelLabel } from '@/helpers/campaigns/pricingModel.helper';
import { formatMediaChannel } from '@/helpers/ioSettings/ioSettings.helper.ts';
import { getBaseCurrencyByCountry } from '@/services/common.service';
import {
  DeviatedLineItem,
  DeviatedLineItemsRequestBody,
  diagnoseLineItem,
  getDeviatedLineItems
} from '@/services/lineItem.service';
import { syncLineItem } from '@/services/lineItemId.service';
import { useGlobalStore } from '@/store/global';
import { getBaseCurrency } from '@/store/global/baseCurrency/selectors.ts';
import { getUser } from '@/store/global/user/selectors.ts';
import Subtitle from '../ListComponents/Subtitle/Subtitle';
import { EntityListStyle } from '../Shared/Shared.styled';
import { DEVIATED_LINE_ITEMS_HIDEABLE_COLUMN_IDS } from './deviatedLineItemsColumns';
import './deviatedLineItemsList.scss';

import type {
  MultiFlightEntityState,
  MultiFlightEntityStateResponse,
  MultiFlightSyncErrorDetail
} from 'sonata-static-storage/lib/common/common.types';
import type { BaseCurrencyInterface } from 'sonata-static-storage/lib/schemas/baseCurrency/baseCurrency.schema';
import type { CampaignInterface } from 'sonata-static-storage/lib/schemas/campaign/campaign.schema';

type TaptapListItem = {
  atr?: number | null;
  audioComplete?: number | null;
  bidderRetriesText: string;
  clicks?: number | null;
  ctr?: number | null;
  currencyCountry?: string | null;
  // ponytail: column hidden per product ask, but the metric stays fetched/mapped so it can be
  // re-added without touching fetchData again.
  dataFeeTotalUser?: number | null;
  deviationTotalSpentUser: number;
  expectedMonthUser?: number | null;
  expectedTodayUsd?: number | null;
  expectedTodayUser?: number | null;
  footfallAttribution?: number | null;
  forecastTodayUser?: number | null;
  forecastUser?: number | null;
  id: string;
  impressions?: number | null;
  lastUpdateText: string;
  lastUpdateTimeText: string | null;
  lineItem: DeviatedLineItem;
  mediaChannelText: string;
  monthDeviationPercentage: number | null;
  opRisk: DeviatedLineItem['opRisk'];
  pacingStatusProps: {
    entityId: string;
    getPacing: () => Promise<Awaited<ReturnType<typeof getEntityDiagnostic>>>;
    isInDraft: boolean;
    onClick?: () => void;
  };
  pricingModelText: string;
  sonataAccountName: string;
  statusProps: {
    entityId: string;
    getState: () => Promise<MultiFlightEntityStateResponse>;
    onSyncButtonClick: () => Promise<void>;
    queryKey: string[];
  };
  storeVisitRate?: number | null;
  subtitleProps: { campaignPlanName: string; ioName: string; type: 'lineItem' };
  titleProps: { text: string };
  todayDeviationPercentage: number | null;
  totalGrossMargin?: number | null;
  totalSpendUsd?: number | null;
  traderText: string;
  videoComplete?: number | null;
  visit?: number | null;
  vtr?: number | null;
  webVisitRate?: number | null;
};

type State = {
  changelogConfig?: { entityId: string; show: boolean; title: string; userId: string };
  entityPropsPacingClicked?: {
    lineItemId: string;
    userId: string;
  };
  forecastLineItem?: CampaignInterface;
  modalCurrency?: BaseCurrencyInterface;
};

type Props = {
  cacheSeed: string;
  isColumnVisible: (columnId: string) => boolean;
  onListChange: () => void;
  onTotalItemsChange?: (total: number) => void;
  requestBody: Omit<DeviatedLineItemsRequestBody, 'itemsPerPage' | 'page'>;
};

const PAGE_SIZE = 20;
// Always-rendered columns: pacing, name, resizer and actions.
const FIXED_COLUMNS = 4;

// Row height (deviatedLineItemsList.scss `tbody td { height: 58px }`) plus the 8px vertical
// border-spacing gap between rows, and the sticky header's fixed height — used to size the
// initial fetch to just what fills the visible scroll container instead of a flat PAGE_SIZE.
const ROW_HEIGHT = 58 + 8;
const HEADER_HEIGHT = 38;

const getVisiblePageSize = (containerHeight: number | undefined) => {
  if (!containerHeight) {
    return PAGE_SIZE;
  }
  return Math.max(1, Math.floor((containerHeight - HEADER_HEIGHT) / ROW_HEIGHT));
};

const NAME_COL_DEFAULT_WIDTH = 650;
const NAME_COL_MIN_WIDTH = 150;
// pacing + resizer colgroup widths. The name column may grow until the pinned block
// (these two columns plus the name itself) fills the scroll container's visible width.
const PINNED_SIBLING_COLUMNS_WIDTH = 8 + 8;
// The drag stops this far before the container's right edge, so the handle (and its col-resize
// cursor) never ends up flush against the edge where it would be hard to grab back.
const RESIZE_EDGE_GAP = 16;

// ponytail: placeholder columnKey → backend sortBy names. Confirm the real insights-API sort
// keys with the backend team (MTF-636 open item) — until BE+insights forward these, the params
// are sent but do not reorder yet. opRisk is the exception: it's scored and sorted in
// platform-api itself (after the pacing filter), so it does reorder the list.
const SORT_MAP: Record<string, string> = {
  account: 'account',
  bidderRetries: 'bidderFailCounter',
  expectedToday: 'expectedTodayUser',
  goalTarget: 'expectedMonthUser',
  lastUpdate: 'lastUpdate',
  liEndDate: 'endDate',
  mediaChannel: 'type',
  monthDeviationValue: 'monthDeviationValueUser',
  name: 'name',
  opRisk: 'opRisk',
  pricingModel: 'pricingModel',
  todayDeviationValue: 'todayDeviationPercentage',
  todayDeviationValueUsd: 'todayDeviationValueUsd',
  totalSpend: 'totalSpentUser',
  trader: 'trader'
};

const OP_RISK_TAG_STATUS = { high: 'wrong', low: 'ok', medium: 'warning' } as const;

const OP_RISK_LABELS: Record<DeviatedLineItem['opRisk']['level'], { defaultMessage: string; id: string }> = {
  high: { defaultMessage: 'High', id: 'deviatedLineItems.list.opRisk.high' },
  low: { defaultMessage: 'Low', id: 'deviatedLineItems.list.opRisk.low' },
  medium: { defaultMessage: 'Medium', id: 'deviatedLineItems.list.opRisk.medium' }
};

const OP_RISK_LEVEL_DESCRIPTIONS: Record<DeviatedLineItem['opRisk']['level'], { defaultMessage: string; id: string }> =
  {
    high: { defaultMessage: 'Needs urgent action', id: 'deviatedLineItems.list.tooltip.opRiskInfo.high' },
    low: { defaultMessage: 'On track', id: 'deviatedLineItems.list.tooltip.opRiskInfo.low' },
    medium: { defaultMessage: 'Keep monitoring', id: 'deviatedLineItems.list.tooltip.opRiskInfo.medium' }
  };

const OP_RISK_LEVELS = ['high', 'medium', 'low'] as const;

export default function DeviatedLineItemsList({
  cacheSeed,
  isColumnVisible: isVisible,
  onListChange,
  onTotalItemsChange,
  requestBody
}: Readonly<Props>) {
  const [{ changelogConfig, entityPropsPacingClicked, forecastLineItem, modalCurrency }, setState] = useState<State>(
    {}
  );

  const [listItems, setListItems] = useState<TaptapListItem[]>([]);
  const visibleColumnCount = FIXED_COLUMNS + DEVIATED_LINE_ITEMS_HIDEABLE_COLUMN_IDS.filter(isVisible).length;
  const [currencyByCountry, setCurrencyByCountry] = useState<Record<string, BaseCurrencyInterface>>({});
  const inFlightCountriesRef = useRef<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [sortColumn, setSortColumn] = useState('todayDeviationValueUsd');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const sortBy = SORT_MAP[sortColumn];
  const [nameColWidth, setNameColWidth] = useState(NAME_COL_DEFAULT_WIDTH);
  const [showDisclaimer, setShowDisclaimer] = useState(true);
  const [diagnosisCache, setDiagnosisCache] = useState<
    Record<string, { agentMessage?: string; chatId?: string; userMessage: string }>
  >({});
  const [activeDiagnosisLineItemId, setActiveDiagnosisLineItemId] = useState<string | null>(null);
  const [diagnosingId, setDiagnosingId] = useState<string | null>(null);
  const hasDiagnosticsFeature = useFeatureUser('deviatedLIDiagnostics');

  const lastQueryKeyRef = useRef('');
  const pageSizeRef = useRef(PAGE_SIZE);
  const requestIdRef = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);
  const resizeStartRef = useRef<{ maxWidth: number; width: number; x: number } | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLTableRowElement>(null);

  const currencyInfo = useGlobalStore(getBaseCurrency);
  const user = useGlobalStore(getUser);
  const { id: userId = '' } = user.entity;
  const { formatMessage } = useIntl();

  const onListChangeRef = useRef(onListChange);
  onListChangeRef.current = onListChange;

  const handleCloseModal = () => {
    setState(prevState => ({
      ...prevState,
      entityPropsPacingClicked: undefined
    }));
  };

  const handleForecastModalClose = () => {
    setState(prevState => ({
      ...prevState,
      forecastLineItem: undefined
    }));
  };

  const handleChangelogModalClose = () => {
    setState(prevState => ({
      ...prevState,
      changelogConfig: undefined
    }));
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: cacheSeed is intentionally listed to trigger re-fetches on external invalidation
  const fetchData = useCallback(
    async (params: { page: number; pageSize: number; signal?: AbortSignal }) => {
      try {
        const response = await getDeviatedLineItems(
          userId,
          {
            ...requestBody,
            itemsPerPage: params.pageSize,
            page: params.page,
            ...(sortBy ? { sortBy, sortOrder } : {})
          },
          params.signal
        );

        const items: TaptapListItem[] =
          response?.items?.map(lineItem => {
            const entityDiagnosticPromise = getEntityDiagnostic(userId, lineItem._id as string, 'lineItem');
            const lineItemId = String(lineItem._id);
            const extraData = (lineItem as DeviatedLineItem & { extraData?: Record<string, any> }).extraData;

            return {
              atr: lineItem.metrics?.atr,
              audioComplete: lineItem.metrics?.audioComplete,
              bidderRetriesText: lineItem.launching
                ? String(lineItem.syncRetries ?? lineItem.bidderFailCounter ?? 0)
                : '-',
              clicks: lineItem.metrics?.clicks,
              ctr: lineItem.metrics?.ctr,
              currencyCountry: lineItem.account?.currencyCountry,
              dataFeeTotalUser: lineItem.metrics?.dataFeeTotalUser,
              deviationTotalSpentUser: lineItem.deviationData.totalSpentUser,
              expectedMonthUser: lineItem.deviationData.expectedMonthUser,
              expectedTodayUsd: lineItem.deviationData.expectedToday,
              expectedTodayUser: lineItem.deviationData.expectedTodayUser,
              footfallAttribution: lineItem.metrics?.footfallAttribution,
              forecastTodayUser: lineItem.deviationData.forecastTodayUser,
              forecastUser: lineItem.deviationData.forecastUser,
              id: lineItemId,
              impressions: lineItem.metrics?.impressions,
              lastUpdateText: lineItem.deviationData.lastUpdate
                ? moment.utc(lineItem.deviationData.lastUpdate).format('DD/MM/YYYY')
                : '-',
              lastUpdateTimeText: lineItem.deviationData.lastUpdate
                ? moment.utc(lineItem.deviationData.lastUpdate).tz(lineItem.timezone).format('YYYY/MM/DD HH:mm:ss')
                : null,
              lineItem,
              mediaChannelText: lineItem.type ? formatMediaChannel(lineItem.type) : '-',
              monthDeviationPercentage: lineItem.deviationData.monthDeviationPercentage,
              opRisk: lineItem.opRisk,
              pacingStatusProps: {
                entityId: lineItemId,
                getPacing: () => entityDiagnosticPromise,
                isInDraft: !lineItem.completed,
                onClick: lineItem.completed
                  ? () =>
                      setState(prev => ({
                        ...prev,
                        entityPropsPacingClicked: { lineItemId: lineItem._id as string, userId: lineItem.user }
                      }))
                  : undefined
              },
              pricingModelText: getPricingModelLabel(formatMessage, lineItem.bidModels?.[0]?.bid?.pricingModel),
              sonataAccountName: lineItem.account?.name || '-',
              statusProps: {
                entityId: lineItemId,
                getState: async () => {
                  // getLineItemStatus's declared return type doesn't include paused (it's typed as
                  // Partial<LineItem>), but the endpoint it calls returns it at runtime - same cast
                  // LineItemsList.tsx already relies on for the same call.
                  const result = (await getLineItemStatus(lineItemId)) as unknown as {
                    /** @deprecated alias of updating, kept by the API until MTF-772 step 7 */
                    launching?: boolean;
                    paused?: boolean;
                    state: MultiFlightEntityState;
                    syncError?: MultiFlightSyncErrorDetail;
                    updating?: boolean;
                  };
                  // Same mapping as LineItemsList: paused wins over active (the endpoint reports paused=false
                  // once finished); updating puts the sync badge on the pill, explained with syncError
                  return {
                    state: result.paused ? 'paused' : result.state,
                    updating: Boolean(result.updating ?? result.launching),
                    ...(result.syncError && { syncError: result.syncError })
                  };
                },
                onSyncButtonClick: async () => {
                  try {
                    await syncLineItem(lineItemId);
                  } catch {
                    showNotification(
                      'error',
                      formatMessage({
                        defaultMessage: 'Line item failed to be synced, please try again',
                        id: 'lineItems.list.syncLineItem.error'
                      })
                    );
                  }
                },
                queryKey: [lineItem.modifiedDate as string]
              },
              storeVisitRate: lineItem.metrics?.storeVisitRate,
              subtitleProps: {
                campaignPlanName: extraData?.cpName || 'N/A',
                ioName: extraData?.ioName || 'N/A',
                type: 'lineItem' as const
              },
              titleProps: {
                text: lineItem.name as string
              },
              todayDeviationPercentage: lineItem.deviationData.todayDeviationPercentage,
              totalGrossMargin: lineItem.metrics?.totalGrossMargin,
              totalSpendUsd: lineItem.deviationData.totalSpend,
              traderText: lineItem.trader?.name || lineItem.trader?.email || '-',
              videoComplete: lineItem.metrics?.videoComplete,
              visit: lineItem.metrics?.visit,
              vtr: lineItem.metrics?.vtr,
              webVisitRate: lineItem.metrics?.webVisitRate
            };
          }) ?? [];

        onTotalItemsChange?.(response.totalItems);

        return {
          data: items,
          totalPages: Math.ceil(response.totalItems / params.pageSize)
        };
      } catch {
        // Superseded by a newer request - not a failure, and its result is discarded by the caller anyway
        if (!params.signal?.aborted) {
          showNotification(
            'error',
            formatMessage({
              defaultMessage: 'Failed to load line items',
              id: 'lineItems.fetchData.error'
            })
          );
        }
        return { data: [], totalPages: 0 };
      }
    },
    [userId, requestBody, cacheSeed, formatMessage, sortBy, sortOrder]
  );

  // Any new list request (filter/sort change or next page) aborts the one still in flight
  const startRequest = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    return { requestId: ++requestIdRef.current, signal: abortControllerRef.current.signal };
  }, []);

  useEffect(() => () => abortControllerRef.current?.abort(), []);

  const loadInitialData = useCallback(async () => {
    const { requestId, signal } = startRequest();
    setListItems([]);
    setPage(1);
    setIsFetching(true);
    // The loading spinner is absolutely positioned within this scrollable container, so it scrolls
    // with it - without resetting both axes here, it renders out of view (or only partially covers
    // the viewport) whenever a new fetch starts (filter/sort change) while the user was scrolled
    // down and/or right in the previous result set - sorting a far-right column is the common way
    // to end up scrolled right when this fires.
    scrollRef.current?.scrollTo({ left: 0, top: 0 });
    pageSizeRef.current = getVisiblePageSize(scrollRef.current?.clientHeight);
    try {
      const response = await fetchData({ page: 1, pageSize: pageSizeRef.current, signal });
      if (requestIdRef.current !== requestId) {
        return;
      }
      setListItems(response.data);
      setHasMore(response.totalPages > 1);
    } finally {
      if (requestIdRef.current === requestId) {
        setIsFetching(false);
      }
    }
  }, [fetchData, startRequest]);

  // Reset to page 1 and refetch whenever the query changes (filters via requestBody, cacheSeed, or
  // sort). Gate on the query VALUE, not fetchData's identity: a filter reset emits two store writes
  // ({} then {advanceFilters:[]}) that yield an identical query but a fresh requestBody object each
  // time, which would otherwise fire two fetches. cacheSeed stays in the key so force-resync (same
  // filters, bumped seed) still refetches.
  const queryKey = `${cacheSeed}|${sortBy}|${sortOrder}|${JSON.stringify(requestBody)}`;
  useEffect(() => {
    if (lastQueryKeyRef.current === queryKey) {
      return;
    }
    lastQueryKeyRef.current = queryKey;
    loadInitialData();
  }, [queryKey, loadInitialData]);

  const loadMore = useCallback(async () => {
    if (!hasMore || isFetching) {
      return;
    }
    const { requestId, signal } = startRequest();
    setIsFetching(true);
    try {
      const nextPage = page + 1;
      const response = await fetchData({ page: nextPage, pageSize: pageSizeRef.current, signal });
      if (requestIdRef.current !== requestId) {
        return;
      }
      setListItems(prev => uniqBy([...prev, ...response.data], 'id'));
      setPage(nextPage);
      setHasMore(nextPage < response.totalPages);
    } finally {
      if (requestIdRef.current === requestId) {
        setIsFetching(false);
      }
    }
  }, [hasMore, isFetching, page, fetchData, startRequest]);

  const loadMoreRef = useRef(loadMore);
  loadMoreRef.current = loadMore;

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) {
      return;
    }
    const observer = new IntersectionObserver(entries => {
      if (entries[0]?.isIntersecting) {
        loadMoreRef.current();
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
    // Re-observing on every listItems change forces a fresh intersection check: since the initial
    // page is sized to exactly fill the container, the sentinel can stay continuously visible across
    // a fetch (no overflow to scroll), and the observer only fires on a threshold crossing — without
    // this, hasMore flipping true after that first (too-early) fire would never trigger loadMore again.
  }, [listItems]);

  useEffect(() => {
    const pending = [...new Set(listItems.map(item => item.currencyCountry).filter((c): c is string => !!c))].filter(
      country => !(country in currencyByCountry) && !inFlightCountriesRef.current.has(country)
    );
    pending.forEach(country => {
      inFlightCountriesRef.current.add(country);
      getBaseCurrencyByCountry(country, {})
        .then((currency: BaseCurrencyInterface) => {
          setCurrencyByCountry(prev => ({ ...prev, [country]: currency }));
        })
        .finally(() => {
          inFlightCountriesRef.current.delete(country);
        });
    });
  }, [listItems, currencyByCountry]);

  const handleResizeStart = (event: ReactPointerEvent<HTMLDivElement>) => {
    // preventDefault stops text selection from starting while dragging across the table.
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    // clientWidth measures the visible area (scrollbar excluded), so the pinned block can grow to
    // fill the container but never push its own edge out of view.
    const containerWidth = scrollRef.current?.clientWidth ?? NAME_COL_DEFAULT_WIDTH + PINNED_SIBLING_COLUMNS_WIDTH;
    resizeStartRef.current = {
      maxWidth: containerWidth - PINNED_SIBLING_COLUMNS_WIDTH - RESIZE_EDGE_GAP,
      width: nameColWidth,
      x: event.clientX
    };
  };

  const handleResizeMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const start = resizeStartRef.current;
    if (!start) {
      return;
    }
    const width = start.width + event.clientX - start.x;
    setNameColWidth(Math.min(start.maxWidth, Math.max(NAME_COL_MIN_WIDTH, width)));
  };

  const handleResizeEnd = () => {
    resizeStartRef.current = null;
  };

  // Rendered inside the dedicated resizer column's (sticky) header and body cells — the pinned
  // cells are the only elements reliably sitting on the Title/Channel edge at every scroll position.
  const resizeHandle = (
    <div
      aria-hidden
      className="deviatedLineItemsList__resizeHandle"
      onPointerDown={handleResizeStart}
      onPointerMove={handleResizeMove}
      onPointerUp={handleResizeEnd}
    />
  );

  // Static visual divider on the pinned actions column's left edge — same overlay technique as
  // resizeHandle above, but non-interactive since actions isn't resizable.
  const actionsDivider = <div aria-hidden className="deviatedLineItemsList__actionsDivider" />;

  const handleSort = (columnKey: string, order: 'asc' | 'desc') => {
    setSortColumn(columnKey);
    setSortOrder(order);
  };

  const getSortIconColors = (columnKey: string): { down: string; up: string } => {
    const inactive = 'var(--color-sonata-primary-500)';
    if (sortColumn !== columnKey) {
      return { down: inactive, up: inactive };
    }
    const active = 'var(--color-sonata-primary-700)';
    return {
      down: sortOrder === 'desc' ? active : inactive,
      up: sortOrder === 'asc' ? active : inactive
    };
  };

  const renderSortHeader = (columnKey: string, label: string, infoTooltip?: ReactNode) => {
    const sortIconColors = getSortIconColors(columnKey);
    // Arrow clicks stop propagation so they pick their own explicit direction instead of
    // falling through to the button's default (label) click handler below.
    const handleArrowClick = (order: 'asc' | 'desc') => (event: ReactMouseEvent<HTMLElement>) => {
      event.stopPropagation();
      handleSort(columnKey, order);
    };
    // Label click: default to asc for a column that isn't currently sorted, otherwise flip
    // the active column's current direction.
    const handleLabelClick = () => {
      const isActive = sortColumn === columnKey;
      handleSort(columnKey, isActive && sortOrder === 'asc' ? 'desc' : 'asc');
    };
    return (
      <div className="deviatedLineItemsList__headerCell">
        <button
          className="deviatedLineItemsList__sortHeader"
          disabled={isFetching}
          onClick={handleLabelClick}
          type="button"
        >
          <span>{label}</span>
          {infoTooltip}
          <span aria-hidden className="deviatedLineItemsList__sortIcons">
            <i className="fa-solid fa-sort-up" onClick={handleArrowClick('asc')} style={{ color: sortIconColors.up }} />
            <i
              className="fa-solid fa-sort-down"
              onClick={handleArrowClick('desc')}
              style={{ color: sortIconColors.down }}
            />
          </span>
        </button>
      </div>
    );
  };

  const renderRowActions = (item: TaptapListItem, rowCurrency: BaseCurrencyInterface) => {
    const lineItemId = item.id;
    const comingSoonTooltip = (
      <span className="deviatedLineItemsList__tooltipBody">
        {formatMessage({ defaultMessage: 'Coming soon', id: 'deviatedLineItems.list.actions.comingSoon' })}
      </span>
    );

    return (
      <ContextualMenu
        buttonConfig={{ icon: 'far fa-ellipsis-v', shape: 'link', size: 'ultraSmall', type: 'secondary' }}
        direction="vertical"
        id={`deviatedLI-actions-${lineItemId}`}
      >
        <ContextualMenu.Item id={`deviatedLI-diagnostic-${lineItemId}`}>
          <ButtonUI
            disabled={!hasDiagnosticsFeature || diagnosingId === lineItemId}
            icon="fa-regular fa-stethoscope"
            id={`deviatedLI-diagnostic-btn-${lineItemId}`}
            onClick={async () => {
              if (diagnosisCache[lineItemId]) {
                setActiveDiagnosisLineItemId(lineItemId);
                return;
              }
              if (diagnosingId) {
                return;
              }
              setDiagnosingId(lineItemId);
              const userMessage = `I would like to diagnose the line item ${lineItemId}. If this line item requires enabling the analytical diagnosis funnel first, please enable it automatically and proceed without asking me for confirmation.`;
              setDiagnosisCache(prev => ({ ...prev, [lineItemId]: { userMessage } }));
              setActiveDiagnosisLineItemId(lineItemId);
              try {
                const { chatId, message } = await diagnoseLineItem(item.lineItem.user as string, lineItemId);
                setDiagnosisCache(prev =>
                  prev[lineItemId]
                    ? { ...prev, [lineItemId]: { ...prev[lineItemId], agentMessage: message, chatId } }
                    : prev
                );
              } catch {
                setDiagnosisCache(prev => {
                  const { [lineItemId]: _removed, ...rest } = prev;
                  return rest;
                });
                setActiveDiagnosisLineItemId(prev => (prev === lineItemId ? null : prev));
                showNotification(
                  'error',
                  formatMessage({
                    defaultMessage: 'Failed to diagnose line item',
                    id: 'deviatedLineItems.list.actions.diagnostic.error'
                  })
                );
              } finally {
                setDiagnosingId(null);
              }
            }}
            shape="link"
            size="ultraSmall"
            text={formatMessage({
              defaultMessage: 'Diagnostic',
              id: 'deviatedLineItems.list.actions.diagnostic'
            })}
            type="secondary"
          />
        </ContextualMenu.Item>
        <ContextualMenu.Item id={`deviatedLI-checkErrors-${lineItemId}`}>
          <Tooltip
            config={{
              component: comingSoonTooltip,
              interactions: [TooltipInteractionEnum.hover],
              placement: 'top',
              portal: true,
              strategy: 'fixed'
            }}
          >
            <span className="deviatedLineItemsList__disabledActionWrapper">
              <ButtonUI
                disabled
                icon="fa-regular fa-triangle-exclamation"
                id={`deviatedLI-checkErrors-btn-${lineItemId}`}
                shape="link"
                size="ultraSmall"
                text={formatMessage({
                  defaultMessage: 'Check errors',
                  id: 'deviatedLineItems.list.actions.checkErrors'
                })}
                type="secondary"
              />
            </span>
          </Tooltip>
        </ContextualMenu.Item>
        <ContextualMenu.Item id={`deviatedLI-obtainDto-${lineItemId}`}>
          <Tooltip
            config={{
              component: comingSoonTooltip,
              interactions: [TooltipInteractionEnum.hover],
              placement: 'top',
              portal: true,
              strategy: 'fixed'
            }}
          >
            <span className="deviatedLineItemsList__disabledActionWrapper">
              <ButtonUI
                disabled
                icon="fa-regular fa-file-export"
                id={`deviatedLI-obtainDto-btn-${lineItemId}`}
                shape="link"
                size="ultraSmall"
                text={formatMessage({
                  defaultMessage: 'Obtain DTO',
                  id: 'deviatedLineItems.list.actions.obtainDto'
                })}
                type="secondary"
              />
            </span>
          </Tooltip>
        </ContextualMenu.Item>
        <ContextualMenu.Item
          icon="fa-regular fa-arrows-rotate"
          id={`deviatedLI-forceResync-${lineItemId}`}
          onClick={async () => {
            try {
              await syncLineItem(lineItemId);
              onListChangeRef.current();
            } catch {
              showNotification(
                'error',
                formatMessage({
                  defaultMessage: 'Line item failed to be synced, please try again',
                  id: 'lineItems.list.syncLineItem.error'
                })
              );
            }
          }}
          text={formatMessage({ defaultMessage: 'Force resync', id: 'deviatedLineItems.list.actions.forceResync' })}
        />
        <ContextualMenu.Item
          icon="far fa-copy"
          id={`deviatedLI-copyId-${lineItemId}`}
          onClick={() => handleCopyId(lineItemId)}
          text={formatMessage({ defaultMessage: 'Copy Line Item ID', id: 'deviatedLineItems.list.actions.copyId' })}
        />
        <ContextualMenu.Item
          disabled={!item.lineItem.completed}
          icon="fa-regular fa-list"
          id={`deviatedLI-changelog-${lineItemId}`}
          onClick={() =>
            setState(prev => ({
              ...prev,
              changelogConfig: {
                entityId: lineItemId,
                show: true,
                title: item.titleProps.text,
                userId: item.lineItem.user as string
              }
            }))
          }
          showSeparator
          text={formatMessage({ defaultMessage: 'Changelog', id: 'deviatedLineItems.list.actions.changelog' })}
        />
        <ContextualMenu.Item
          icon="fa-regular fa-signal-bars"
          id={`deviatedLI-forecast-${lineItemId}`}
          onClick={() =>
            setState(prev => ({
              ...prev,
              forecastLineItem: item.lineItem as unknown as CampaignInterface,
              modalCurrency: rowCurrency
            }))
          }
          text={formatMessage({ defaultMessage: 'Forecast', id: 'deviatedLineItems.list.actions.forecast' })}
        />
      </ContextualMenu>
    );
  };

  const renderCount = (value: number | null | undefined, currency: BaseCurrencyInterface) => {
    if (value == null) {
      return '-';
    }
    const country = currency.countries?.[0];
    return value >= 1000 ? abbreviateNumber(value, 2, country) : normalize(value, 0, country);
  };
  const renderExactCount = (value: number | null | undefined, currency: BaseCurrencyInterface) =>
    value == null ? '-' : normalize(value, 0, currency.countries?.[0]);
  const renderRate = (value: number | null | undefined, currency: BaseCurrencyInterface) =>
    value == null ? '-' : `${normalize(value, 2, currency.countries?.[0])}%`;
  const renderExactCurrency = (value: number | null | undefined, currency: BaseCurrencyInterface) =>
    value == null ? '-' : normalizeWithUnit(value, currency, 2);
  // Always USD, unlike the other renderers above: expectedMonth/totalSpend are the bare (USD) insights
  // fields, not the row's own account currency.
  const renderExactUsd = (value: number | null | undefined) =>
    value == null ? '-' : normalizeWithUnit(value, { currencyPosition: 'left', currencySymbol: '$' }, 2);
  const isEmptyMetric = (...values: Array<number | null | undefined>) => values.every(value => !value);
  const renderMetricNA = () => <span>-</span>;

  const handleCopyId = async (id: string) => {
    await navigator.clipboard.writeText(id);
    showNotification(
      'success',
      formatMessage({
        defaultMessage: 'Line item ID copied to clipboard',
        id: 'deviatedLineItems.list.copyId.success'
      })
    );
  };

  // A line is either a plain narrative string, or a { label, value } pair whose label renders bold
  // and value doesn't - metric tooltips use the latter; free-form ones (Op. Risk, Days Left) the former.
  type TooltipLine = string | { label: string; value: ReactNode };
  const renderTooltipLine = (line: TooltipLine) =>
    typeof line === 'string' ? (
      line
    ) : (
      <>
        <strong>{line.label}</strong> {line.value}
      </>
    );

  const tooltipLines = (line1: TooltipLine, ...otherLines: Array<TooltipLine | undefined>) => (
    <div className="deviatedLineItemsList__tooltipBody">
      <div>{renderTooltipLine(line1)}</div>
      {otherLines.map((line, index) => line != null && <div key={index}>{renderTooltipLine(line)}</div>)}
    </div>
  );

  const renderMetricCell = (isEmpty: boolean, tooltipComponent: ReactNode, content: ReactNode) =>
    isEmpty ? (
      <div className="metricStack">{renderMetricNA()}</div>
    ) : (
      <Tooltip
        config={{
          component: tooltipComponent,
          interactions: [TooltipInteractionEnum.hover],
          placement: 'top',
          strategy: 'fixed'
        }}
      >
        <div className="metricStack">{content}</div>
      </Tooltip>
    );

  const isEmptyState = !isFetching && listItems.length === 0;

  const mostRecentUpdate = listItems.reduce<moment.Moment | null>((latest, item) => {
    const raw = item.lineItem.deviationData?.lastUpdate;
    if (!raw) {
      return latest;
    }
    const current = moment.utc(raw);
    return !latest || current.isAfter(latest) ? current : latest;
  }, null);

  return (
    <EntityListStyle
      className="deviatedLineItemsList"
      style={{ '--pin-name-width': `${nameColWidth}px` } as CSSProperties}
    >
      {!!mostRecentUpdate && showDisclaimer && (
        <Toast
          autoClose={false}
          className="deviatedLineItemsList__disclaimer"
          closeToast={() => setShowDisclaimer(false)}
          icon="fa-regular fa-clock"
          text={
            formatMessage(
              {
                defaultMessage: 'Deviation and performance data reflects updates through {date}.',
                id: 'deviatedLineItems.list.disclaimer.lastUpdate'
              },
              { date: mostRecentUpdate.local().format('DD/MM/YYYY HH:mm') }
            ) as string
          }
          type="info"
        />
      )}
      <div
        className={
          isEmptyState
            ? 'deviatedLineItemsList__scroll deviatedLineItemsList__scroll--empty'
            : 'deviatedLineItemsList__scroll'
        }
        ref={scrollRef}
      >
        <table className="deviatedLineItemsList__table">
          <colgroup>
            {/* pacing */}
            <col style={{ width: '8px' }} />
            {/* name — user-resizable by dragging the resizer column; see --pin-name-width */}
            <col style={{ width: 'var(--pin-name-width)' }} />
            {/* resizer */}
            <col style={{ width: '8px' }} />
            {/* opRisk */}
            {isVisible('opRisk') && <col style={{ width: '110px' }} />}
            {/* mediaChannel */}
            {isVisible('mediaChannel') && <col style={{ width: '100px' }} />}
            {/* pricingModel */}
            {isVisible('pricingModel') && <col style={{ width: '90px' }} />}
            {/* account */}
            {isVisible('account') && <col style={{ width: '140px' }} />}
            {/* trader */}
            {isVisible('trader') && <col style={{ width: '140px' }} />}
            {/* status — no play button in this list, unlike other multiflight lists */}
            {isVisible('status') && <col style={{ width: '120px' }} />}
            {/* endDate */}
            {isVisible('endDate') && <col style={{ width: '100px' }} />}
            {/* todayDeviationValue — 205px minimum so the sort keys fit */}
            {isVisible('todayDeviationValue') && <col style={{ width: '205px' }} />}
            {/* totalSpend — 175px */}
            {isVisible('totalSpend') && <col style={{ width: '175px' }} />}
            {/* expectedToday — 170px, same reasoning as goalTarget: label + info icon + sort arrows */}
            {isVisible('expectedToday') && <col style={{ width: '170px' }} />}
            {/* goalTarget — 170px: "Goal Target" plus its info icon and sort arrows */}
            {isVisible('goalTarget') && <col style={{ width: '170px' }} />}
            {/* monthDeviationValue — 205px minimum, same reasoning as todayDeviationValue */}
            {isVisible('monthDeviationValue') && <col style={{ width: '205px' }} />}
            {/* todayDeviationValueUsd — 205px minimum, same reasoning as todayDeviationValue */}
            {isVisible('todayDeviationValueUsd') && <col style={{ width: '205px' }} />}
            {/* grossMargin */}
            {isVisible('grossMargin') && <col style={{ width: '100px' }} />}
            {/* impressions */}
            {isVisible('impressions') && <col style={{ width: '100px' }} />}
            {/* clicks */}
            {isVisible('clicks') && <col style={{ width: '100px' }} />}
            {/* videoComplete */}
            {isVisible('videoComplete') && <col style={{ width: '100px' }} />}
            {/* audioComplete */}
            {isVisible('audioComplete') && <col style={{ width: '100px' }} />}
            {/* visit */}
            {isVisible('visit') && <col style={{ width: '100px' }} />}
            {/* footfall */}
            {isVisible('footfall') && <col style={{ width: '100px' }} />}
            {/* lastUpdate */}
            {isVisible('lastUpdate') && <col style={{ width: '120px' }} />}
            {/* bidderRetries */}
            {isVisible('bidderRetries') && <col style={{ width: '140px' }} />}
            {/* actions — 24px button + the base 4px horizontal padding on each side */}
            <col style={{ width: '32px' }} />
          </colgroup>
          <thead>
            <tr>
              <th className="multiflightPacing" />
              <th className="multiflightTitle">
                {renderSortHeader(
                  'name',
                  formatMessage({ defaultMessage: 'Line Item Name', id: 'lineItems.list.toolbar.name' })
                )}
              </th>
              <th className="multiflightResizer">{resizeHandle}</th>
              {isVisible('opRisk') && (
                <th className="multiflightOpRisk">
                  {renderSortHeader(
                    'opRisk',
                    formatMessage({ defaultMessage: 'Op. Risk', id: 'deviatedLineItems.list.toolbar.opRisk' }),
                    <IconTooltip
                      icon="fa-regular fa-circle-info"
                      iconColor="var(--color-sonata-primary-500, #7B9FE1) !important"
                      place="top"
                      portal
                    >
                      <div className="deviatedLineItemsList__tooltipBody deviatedLineItemsList__opRiskInfo">
                        <div>
                          {formatMessage({
                            defaultMessage:
                              "Calculated from each Line Item's remaining budget to recover and its days left:",
                            id: 'deviatedLineItems.list.tooltip.opRiskInfo.intro'
                          })}
                        </div>
                        <table className="deviatedLineItemsList__opRiskLegend">
                          <thead>
                            <tr>
                              <th>
                                {formatMessage({
                                  defaultMessage: 'Level',
                                  id: 'deviatedLineItems.list.tooltip.opRiskInfo.levelHeader'
                                })}
                              </th>
                              <th>
                                {formatMessage({
                                  defaultMessage: 'Meaning',
                                  id: 'deviatedLineItems.list.tooltip.opRiskInfo.meaningHeader'
                                })}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {OP_RISK_LEVELS.map(level => (
                              <tr key={level}>
                                <td>
                                  <Tag
                                    id={`deviatedLI-opRiskLegend-${level}`}
                                    rounded
                                    status={OP_RISK_TAG_STATUS[level]}
                                    tiny
                                  >
                                    {formatMessage(OP_RISK_LABELS[level])}
                                  </Tag>
                                </td>
                                <td>{formatMessage(OP_RISK_LEVEL_DESCRIPTIONS[level])}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </IconTooltip>
                  )}
                </th>
              )}
              {isVisible('mediaChannel') && (
                <th className="multiflightMediaChannel">
                  {renderSortHeader(
                    'mediaChannel',
                    formatMessage({ defaultMessage: 'Channel', id: 'deviatedLineItems.list.toolbar.channel' })
                  )}
                </th>
              )}
              {isVisible('pricingModel') && (
                <th className="multiflightPricingModel">
                  {renderSortHeader(
                    'pricingModel',
                    formatMessage({ defaultMessage: 'P. Model', id: 'deviatedLineItems.list.toolbar.pricingModel' })
                  )}
                </th>
              )}
              {isVisible('account') && (
                <th className="multiflightAccount">
                  {renderSortHeader(
                    'account',
                    formatMessage({ defaultMessage: 'Seat', id: 'deviatedLineItems.list.toolbar.seat' })
                  )}
                </th>
              )}
              {isVisible('trader') && (
                <th className="multiflightTrader">
                  {renderSortHeader(
                    'trader',
                    formatMessage({ defaultMessage: 'Trader', id: 'deviatedLineItems.list.toolbar.trader' })
                  )}
                </th>
              )}
              {isVisible('status') && (
                <th className="multiflightStatus">
                  <div className="deviatedLineItemsList__headerCell">
                    {formatMessage({ defaultMessage: 'Status', id: 'lineItems.list.toolbar.status' })}
                  </div>
                </th>
              )}
              {isVisible('endDate') && (
                <th className="multiflightEndDate">
                  {renderSortHeader(
                    'liEndDate',
                    formatMessage({ defaultMessage: 'Days Left', id: 'deviatedLineItems.list.toolbar.daysLeft' })
                  )}
                </th>
              )}
              {isVisible('todayDeviationValue') && (
                <th className="multiflightTodayDeviationValue">
                  {renderSortHeader(
                    'todayDeviationValue',
                    formatMessage({
                      defaultMessage: 'Today Deviation',
                      id: 'deviatedLineItems.list.toolbar.todayDeviationValue'
                    }),
                    <IconTooltip
                      icon="fa-regular fa-circle-info"
                      iconColor="var(--color-sonata-primary-500, #7B9FE1) !important"
                      place="top"
                      portal
                    >
                      <span className="deviatedLineItemsList__tooltipBody">
                        {formatMessage({
                          defaultMessage:
                            "Total spend so far vs the current month's expected delivery up to today. Sorting by this column uses the deviation percentage.",
                          id: 'deviatedLineItems.list.tooltip.todayDeviationValueInfo'
                        })}
                      </span>
                    </IconTooltip>
                  )}
                </th>
              )}
              {isVisible('totalSpend') && (
                <th className="multiflightTotalSpendUsd">
                  {renderSortHeader(
                    'totalSpend',
                    formatMessage({
                      defaultMessage: 'Total Spend',
                      id: 'deviatedLineItems.list.toolbar.totalSpend'
                    }),
                    <IconTooltip
                      icon="fa-regular fa-circle-info"
                      iconColor="var(--color-sonata-primary-500, #7B9FE1) !important"
                      place="top"
                      portal
                    >
                      <span className="deviatedLineItemsList__tooltipBody">
                        {formatMessage({
                          defaultMessage: "The current month's spend so far.",
                          id: 'deviatedLineItems.list.tooltip.totalSpendInfo'
                        })}
                      </span>
                    </IconTooltip>
                  )}
                </th>
              )}
              {isVisible('expectedToday') && (
                <th className="multiflightExpectedTodayUsd">
                  {renderSortHeader(
                    'expectedToday',
                    formatMessage({
                      defaultMessage: 'Exp. Today',
                      id: 'deviatedLineItems.list.toolbar.expectedToday'
                    }),
                    <IconTooltip
                      icon="fa-regular fa-circle-info"
                      iconColor="var(--color-sonata-primary-500, #7B9FE1) !important"
                      place="top"
                      portal
                    >
                      <span className="deviatedLineItemsList__tooltipBody">
                        {formatMessage({
                          defaultMessage: "The current month's expected delivery up to today.",
                          id: 'deviatedLineItems.list.tooltip.expectedTodayInfo'
                        })}
                      </span>
                    </IconTooltip>
                  )}
                </th>
              )}
              {isVisible('goalTarget') && (
                <th className="multiflightGoalTarget">
                  {renderSortHeader(
                    'goalTarget',
                    formatMessage({
                      defaultMessage: 'Goal Target',
                      id: 'deviatedLineItems.list.toolbar.goalTarget'
                    }),
                    <IconTooltip
                      icon="fa-regular fa-circle-info"
                      iconColor="var(--color-sonata-primary-500, #7B9FE1) !important"
                      place="top"
                      portal
                    >
                      <span className="deviatedLineItemsList__tooltipBody">
                        {formatMessage({
                          defaultMessage: "The current month's expected delivery.",
                          id: 'deviatedLineItems.list.tooltip.goalTargetInfo'
                        })}
                      </span>
                    </IconTooltip>
                  )}
                </th>
              )}
              {isVisible('monthDeviationValue') && (
                <th className="multiflightMonthDeviationValue">
                  {renderSortHeader(
                    'monthDeviationValue',
                    formatMessage({
                      defaultMessage: 'Monthly Deviation',
                      id: 'deviatedLineItems.list.toolbar.monthDeviationValue'
                    }),
                    <IconTooltip
                      icon="fa-regular fa-circle-info"
                      iconColor="var(--color-sonata-primary-500, #7B9FE1) !important"
                      place="top"
                      portal
                    >
                      <span className="deviatedLineItemsList__tooltipBody">
                        {formatMessage({
                          defaultMessage: "Total month forecast vs the current month's expected delivery.",
                          id: 'deviatedLineItems.list.tooltip.monthDeviationValueInfo'
                        })}
                      </span>
                    </IconTooltip>
                  )}
                </th>
              )}
              {isVisible('todayDeviationValueUsd') && (
                <th className="multiflightTodayDeviationValue">
                  {renderSortHeader(
                    'todayDeviationValueUsd',
                    formatMessage({
                      defaultMessage: 'Today Deviation (USD)',
                      id: 'deviatedLineItems.list.toolbar.todayDeviationValueUsd'
                    }),
                    <IconTooltip
                      icon="fa-regular fa-circle-info"
                      iconColor="var(--color-sonata-primary-500, #7B9FE1) !important"
                      place="top"
                      portal
                    >
                      <span className="deviatedLineItemsList__tooltipBody">
                        {formatMessage({
                          defaultMessage:
                            "Total spend so far vs the current month's expected delivery up to today, in USD. Sorting by this column uses the absolute deviated amount, not the percentage.",
                          id: 'deviatedLineItems.list.tooltip.todayDeviationValueUsdInfo'
                        })}
                      </span>
                    </IconTooltip>
                  )}
                </th>
              )}
              {isVisible('grossMargin') && (
                <th className="multiflightGrossMargin">
                  <div className="deviatedLineItemsList__headerCell">
                    {formatMessage({
                      defaultMessage: 'Gross Margin',
                      id: 'deviatedLineItems.list.toolbar.grossMargin'
                    })}
                  </div>
                </th>
              )}
              {isVisible('impressions') && (
                <th className="multiflightImpressions">
                  <div className="deviatedLineItemsList__headerCell">
                    {formatMessage({
                      defaultMessage: 'Impressions',
                      id: 'deviatedLineItems.list.toolbar.impressions'
                    })}
                  </div>
                </th>
              )}
              {isVisible('clicks') && (
                <th className="multiflightClicks">
                  <div className="deviatedLineItemsList__headerCell">
                    {formatMessage({ defaultMessage: 'Clicks', id: 'deviatedLineItems.list.toolbar.clicksCtr' })}
                  </div>
                </th>
              )}
              {isVisible('videoComplete') && (
                <th className="multiflightVideoComplete">
                  <div className="deviatedLineItemsList__headerCell">
                    {formatMessage({
                      defaultMessage: 'Video C.',
                      id: 'deviatedLineItems.list.toolbar.videoComplete'
                    })}
                  </div>
                </th>
              )}
              {isVisible('audioComplete') && (
                <th className="multiflightAudioComplete">
                  <div className="deviatedLineItemsList__headerCell">
                    {formatMessage({
                      defaultMessage: 'Audio C.',
                      id: 'deviatedLineItems.list.toolbar.audioComplete'
                    })}
                  </div>
                </th>
              )}
              {isVisible('visit') && (
                <th className="multiflightVisit">
                  <div className="deviatedLineItemsList__headerCell">
                    {formatMessage({ defaultMessage: 'Web Visits', id: 'deviatedLineItems.list.toolbar.webVisit' })}
                  </div>
                </th>
              )}
              {isVisible('footfall') && (
                <th className="multiflightFootfall">
                  <div className="deviatedLineItemsList__headerCell">
                    {formatMessage({
                      defaultMessage: 'Store Visits',
                      id: 'deviatedLineItems.list.toolbar.storeVisit'
                    })}
                  </div>
                </th>
              )}
              {isVisible('lastUpdate') && (
                <th className="multiflightLastUpdate">
                  {renderSortHeader(
                    'lastUpdate',
                    formatMessage({ defaultMessage: 'L. Update', id: 'lineItems.list.toolbar.date' })
                  )}
                </th>
              )}
              {isVisible('bidderRetries') && (
                <th className="multiflightBidderRetries">
                  {renderSortHeader(
                    'bidderRetries',
                    formatMessage({
                      defaultMessage: 'Sync Retries',
                      id: 'deviatedLineItems.list.toolbar.bidderRetries'
                    })
                  )}
                </th>
              )}
              <th className="multiflightActions">{actionsDivider}</th>
            </tr>
          </thead>
          <tbody>
            {listItems.map(item => {
              const rowCurrency = currencyByCountry[item.currencyCountry ?? ''] ?? currencyInfo;
              return (
                <tr className="deviatedLineItemsList__row" key={item.id}>
                  <td className="multiflightPacing">
                    <PacingStatus
                      entityId={item.pacingStatusProps.entityId}
                      getPacing={item.pacingStatusProps.getPacing}
                      isInDraft={item.pacingStatusProps.isInDraft}
                      onClick={
                        item.pacingStatusProps.onClick
                          ? () =>
                              setState(prev => ({
                                ...prev,
                                entityPropsPacingClicked: {
                                  lineItemId: item.id,
                                  userId: item.lineItem.user
                                },
                                modalCurrency: rowCurrency
                              }))
                          : undefined
                      }
                    />
                  </td>
                  <td className="multiflightTitle">
                    <div className="multiflightTitle__main">
                      <Tooltip
                        config={{
                          component: <span className="deviatedLineItemsList__tooltipBody">{item.titleProps.text}</span>,
                          interactions: [TooltipInteractionEnum.hover],
                          placement: 'top',
                          portal: true,
                          strategy: 'fixed'
                        }}
                      >
                        <span className="deviatedLineItemsList__titleText">{item.titleProps.text}</span>
                      </Tooltip>
                    </div>
                    <div className="multiflightTitle__subtitle">
                      <Subtitle
                        campaignPlanName={item.subtitleProps.campaignPlanName}
                        ioName={item.subtitleProps.ioName}
                        type={item.subtitleProps.type}
                      />
                    </div>
                  </td>
                  <td className="multiflightResizer">{resizeHandle}</td>
                  {isVisible('opRisk') && (
                    <td className="multiflightOpRisk">
                      <Tooltip
                        config={{
                          component: tooltipLines(
                            formatMessage(
                              {
                                defaultMessage: '{amount} to recover',
                                id: 'deviatedLineItems.list.tooltip.opRiskAmount'
                              },
                              {
                                amount: renderExactCurrency(
                                  (item.forecastTodayUser ?? 0) - item.deviationTotalSpentUser,
                                  rowCurrency
                                )
                              }
                            ) as string,
                            item.opRisk.daysLeft === null
                              ? (formatMessage({
                                  defaultMessage: 'Ended — no time left to recover',
                                  id: 'deviatedLineItems.list.tooltip.opRiskEnded'
                                }) as string)
                              : (formatMessage(
                                  {
                                    defaultMessage: 'over {days} days left',
                                    id: 'deviatedLineItems.list.tooltip.opRiskDays'
                                  },
                                  { days: item.opRisk.daysLeft }
                                ) as string)
                          ),
                          interactions: [TooltipInteractionEnum.hover],
                          placement: 'top',
                          strategy: 'fixed'
                        }}
                      >
                        <Tag id={`deviatedLI-opRisk-${item.id}`} rounded status={OP_RISK_TAG_STATUS[item.opRisk.level]}>
                          {formatMessage(OP_RISK_LABELS[item.opRisk.level])}
                        </Tag>
                      </Tooltip>
                    </td>
                  )}
                  {isVisible('mediaChannel') && (
                    <td className="multiflightMediaChannel">
                      <span title={item.mediaChannelText}>{item.mediaChannelText}</span>
                    </td>
                  )}
                  {isVisible('pricingModel') && (
                    <td className="multiflightPricingModel">
                      <span>{item.pricingModelText}</span>
                    </td>
                  )}
                  {isVisible('account') && (
                    <td className="multiflightAccount">
                      <span title={item.sonataAccountName}>{item.sonataAccountName}</span>
                    </td>
                  )}
                  {isVisible('trader') && (
                    <td className="multiflightTrader">
                      <span title={item.traderText}>{item.traderText}</span>
                    </td>
                  )}
                  {isVisible('status') && (
                    <td className="multiflightStatus">
                      <div className="deviatedLineItemsList__statusCell">
                        <Status
                          entityId={item.statusProps.entityId}
                          getState={item.statusProps.getState}
                          onSyncButtonClick={item.statusProps.onSyncButtonClick}
                          queryKey={item.statusProps.queryKey}
                        />
                      </div>
                    </td>
                  )}
                  {isVisible('endDate') && (
                    <td className="multiflightEndDate">
                      <Tooltip
                        config={{
                          component: tooltipLines(
                            {
                              label: formatMessage({
                                defaultMessage: 'Days Left:',
                                id: 'deviatedLineItems.list.tooltip.daysLeft'
                              }),
                              value:
                                item.opRisk.daysLeft === null
                                  ? formatMessage({
                                      defaultMessage: 'Ended',
                                      id: 'deviatedLineItems.list.tooltip.daysLeftEnded'
                                    })
                                  : item.opRisk.daysLeft
                            },
                            {
                              label: formatMessage({
                                defaultMessage: 'Begin date:',
                                id: 'deviatedLineItems.list.tooltip.beginDate'
                              }),
                              value: moment.utc(item.lineItem.beginDate).format('YYYY/MM/DD')
                            },
                            {
                              label: formatMessage({
                                defaultMessage: 'End date:',
                                id: 'deviatedLineItems.list.tooltip.endDate'
                              }),
                              value: moment.utc(item.lineItem.endDate).format('YYYY/MM/DD')
                            }
                          ),
                          interactions: [TooltipInteractionEnum.hover],
                          placement: 'top',
                          strategy: 'fixed'
                        }}
                      >
                        <span>
                          {item.opRisk.daysLeft === null
                            ? formatMessage({
                                defaultMessage: 'Ended',
                                id: 'deviatedLineItems.list.cell.daysLeftEnded'
                              })
                            : formatMessage(
                                { defaultMessage: '{days} days', id: 'deviatedLineItems.list.cell.daysLeft' },
                                { days: item.opRisk.daysLeft }
                              )}
                        </span>
                      </Tooltip>
                    </td>
                  )}
                  {isVisible('todayDeviationValue') && (
                    <td className="multiflightTodayDeviationValue">
                      <Tooltip
                        config={{
                          component: tooltipLines(
                            {
                              label: formatMessage({
                                defaultMessage: 'Total Spend:',
                                id: 'deviatedLineItems.list.tooltip.todayDeviationValueTotalSpend'
                              }),
                              value: renderExactCurrency(item.deviationTotalSpentUser, rowCurrency)
                            },
                            {
                              label: formatMessage({
                                defaultMessage: 'Expected Today:',
                                id: 'deviatedLineItems.list.tooltip.todayDeviationValueExpectedToday'
                              }),
                              value: renderExactCurrency(item.expectedTodayUser, rowCurrency)
                            },
                            {
                              label: formatMessage({
                                defaultMessage: 'Deviation:',
                                id: 'deviatedLineItems.list.tooltip.todayDeviation'
                              }),
                              value: renderRate(item.todayDeviationPercentage, rowCurrency)
                            }
                          ),
                          interactions: [TooltipInteractionEnum.hover],
                          placement: 'top',
                          strategy: 'fixed'
                        }}
                      >
                        <span>
                          {normalizeWithUnitAndThousandAndMillions(
                            Math.abs((item.deviationTotalSpentUser || 0) - (item.expectedTodayUser || 0)),
                            rowCurrency
                          )}
                          {` (${renderRate(item.todayDeviationPercentage, rowCurrency)})`}
                        </span>
                      </Tooltip>
                    </td>
                  )}
                  {isVisible('totalSpend') && (
                    <td className="multiflightTotalSpendUsd">
                      {renderMetricCell(
                        isEmptyMetric(item.deviationTotalSpentUser),
                        tooltipLines({
                          label: formatMessage({
                            defaultMessage: 'Total Spend:',
                            id: 'deviatedLineItems.list.tooltip.totalSpend'
                          }),
                          value: renderExactCurrency(item.deviationTotalSpentUser, rowCurrency)
                        }),
                        <span>{renderExactCurrency(item.deviationTotalSpentUser, rowCurrency)}</span>
                      )}
                    </td>
                  )}
                  {isVisible('expectedToday') && (
                    <td className="multiflightExpectedTodayUsd">
                      {renderMetricCell(
                        isEmptyMetric(item.expectedTodayUser),
                        tooltipLines({
                          label: formatMessage({
                            defaultMessage: 'Expected Today:',
                            id: 'deviatedLineItems.list.tooltip.expectedToday'
                          }),
                          value: renderExactCurrency(item.expectedTodayUser, rowCurrency)
                        }),
                        <span>{renderExactCurrency(item.expectedTodayUser, rowCurrency)}</span>
                      )}
                    </td>
                  )}
                  {isVisible('goalTarget') && (
                    <td className="multiflightGoalTarget">
                      {renderMetricCell(
                        isEmptyMetric(item.expectedMonthUser),
                        tooltipLines({
                          label: formatMessage({
                            defaultMessage: 'Goal Target:',
                            id: 'deviatedLineItems.list.tooltip.goalTarget'
                          }),
                          value: renderExactCurrency(item.expectedMonthUser, rowCurrency)
                        }),
                        <span>{renderExactCurrency(item.expectedMonthUser, rowCurrency)}</span>
                      )}
                    </td>
                  )}
                  {isVisible('monthDeviationValue') && (
                    <td className="multiflightMonthDeviationValue">
                      <Tooltip
                        config={{
                          component: tooltipLines(
                            {
                              label: formatMessage({
                                defaultMessage: 'Forecast Month:',
                                id: 'deviatedLineItems.list.tooltip.monthDeviationValueForecast'
                              }),
                              value: renderExactCurrency(item.forecastUser, rowCurrency)
                            },
                            {
                              label: formatMessage({
                                defaultMessage: 'Expected Month:',
                                id: 'deviatedLineItems.list.tooltip.monthDeviationValueExpectedMonth'
                              }),
                              value: renderExactCurrency(item.expectedMonthUser, rowCurrency)
                            },
                            {
                              label: formatMessage({
                                defaultMessage: 'Deviation:',
                                id: 'deviatedLineItems.list.tooltip.monthDeviation'
                              }),
                              value: renderRate(item.monthDeviationPercentage, rowCurrency)
                            }
                          ),
                          interactions: [TooltipInteractionEnum.hover],
                          placement: 'top',
                          strategy: 'fixed'
                        }}
                      >
                        <span>
                          {normalizeWithUnitAndThousandAndMillions(
                            Math.abs((item.forecastUser || 0) - (item.expectedMonthUser || 0)),
                            rowCurrency
                          )}
                          {` (${renderRate(item.monthDeviationPercentage, rowCurrency)})`}
                        </span>
                      </Tooltip>
                    </td>
                  )}
                  {isVisible('todayDeviationValueUsd') && (
                    <td className="multiflightTodayDeviationValue">
                      <Tooltip
                        config={{
                          component: tooltipLines(
                            {
                              label: formatMessage({
                                defaultMessage: 'Total Spend (USD):',
                                id: 'deviatedLineItems.list.tooltip.todayDeviationValueTotalSpendUsd'
                              }),
                              value: renderExactUsd(item.totalSpendUsd)
                            },
                            {
                              label: formatMessage({
                                defaultMessage: 'Expected Today (USD):',
                                id: 'deviatedLineItems.list.tooltip.todayDeviationValueExpectedTodayUsd'
                              }),
                              value: renderExactUsd(item.expectedTodayUsd)
                            },
                            {
                              label: formatMessage({
                                defaultMessage: 'Deviation:',
                                id: 'deviatedLineItems.list.tooltip.todayDeviationUsd'
                              }),
                              value: renderRate(item.todayDeviationPercentage, rowCurrency)
                            }
                          ),
                          interactions: [TooltipInteractionEnum.hover],
                          placement: 'top',
                          strategy: 'fixed'
                        }}
                      >
                        <span>
                          {normalizeWithUnitAndThousandAndMillions(
                            Math.abs((item.totalSpendUsd || 0) - (item.expectedTodayUsd || 0)),
                            { currencyPosition: 'left', currencySymbol: '$' }
                          )}
                          {` (${renderRate(item.todayDeviationPercentage, rowCurrency)})`}
                        </span>
                      </Tooltip>
                    </td>
                  )}
                  {isVisible('grossMargin') && (
                    <td className="multiflightGrossMargin">
                      {renderMetricCell(
                        isEmptyMetric(item.totalGrossMargin),
                        tooltipLines({
                          label: formatMessage({
                            defaultMessage: 'Gross Margin:',
                            id: 'deviatedLineItems.list.tooltip.grossMargin'
                          }),
                          value: renderRate(item.totalGrossMargin, rowCurrency)
                        }),
                        <span>{renderRate(item.totalGrossMargin, rowCurrency)}</span>
                      )}
                    </td>
                  )}
                  {isVisible('impressions') && (
                    <td className="multiflightImpressions">
                      {renderMetricCell(
                        isEmptyMetric(item.impressions),
                        tooltipLines({
                          label: formatMessage({
                            defaultMessage: 'Impressions:',
                            id: 'deviatedLineItems.list.tooltip.impressions'
                          }),
                          value: renderExactCount(item.impressions, rowCurrency)
                        }),
                        <span>{renderCount(item.impressions, rowCurrency)}</span>
                      )}
                    </td>
                  )}
                  {isVisible('clicks') && (
                    <td className="multiflightClicks">
                      {renderMetricCell(
                        isEmptyMetric(item.clicks, item.ctr),
                        tooltipLines(
                          {
                            label: formatMessage({
                              defaultMessage: 'Clicks:',
                              id: 'deviatedLineItems.list.tooltip.clicks'
                            }),
                            value: renderExactCount(item.clicks, rowCurrency)
                          },
                          {
                            label: formatMessage({ defaultMessage: 'CTR:', id: 'deviatedLineItems.list.tooltip.ctr' }),
                            value: renderRate(item.ctr, rowCurrency)
                          }
                        ),
                        <>
                          <span>{renderCount(item.clicks, rowCurrency)}</span>
                          <span>{renderRate(item.ctr, rowCurrency)}</span>
                        </>
                      )}
                    </td>
                  )}
                  {isVisible('videoComplete') && (
                    <td className="multiflightVideoComplete">
                      {renderMetricCell(
                        isEmptyMetric(item.videoComplete, item.vtr),
                        tooltipLines(
                          {
                            label: formatMessage({
                              defaultMessage: 'Video Completes:',
                              id: 'deviatedLineItems.list.tooltip.videoComplete'
                            }),
                            value: renderExactCount(item.videoComplete, rowCurrency)
                          },
                          {
                            label: formatMessage({ defaultMessage: 'VTR:', id: 'deviatedLineItems.list.tooltip.vtr' }),
                            value: renderRate(item.vtr, rowCurrency)
                          }
                        ),
                        <>
                          <span>{renderCount(item.videoComplete, rowCurrency)}</span>
                          <span>{renderRate(item.vtr, rowCurrency)}</span>
                        </>
                      )}
                    </td>
                  )}
                  {isVisible('audioComplete') && (
                    <td className="multiflightAudioComplete">
                      {renderMetricCell(
                        isEmptyMetric(item.audioComplete, item.atr),
                        tooltipLines(
                          {
                            label: formatMessage({
                              defaultMessage: 'Audio Completes:',
                              id: 'deviatedLineItems.list.tooltip.audioComplete'
                            }),
                            value: renderExactCount(item.audioComplete, rowCurrency)
                          },
                          {
                            label: formatMessage({ defaultMessage: 'ATR:', id: 'deviatedLineItems.list.tooltip.atr' }),
                            value: renderRate(item.atr, rowCurrency)
                          }
                        ),
                        <>
                          <span>{renderCount(item.audioComplete, rowCurrency)}</span>
                          <span>{renderRate(item.atr, rowCurrency)}</span>
                        </>
                      )}
                    </td>
                  )}
                  {isVisible('visit') && (
                    <td className="multiflightVisit">
                      {renderMetricCell(
                        isEmptyMetric(item.visit, item.webVisitRate),
                        tooltipLines(
                          {
                            label: formatMessage({
                              defaultMessage: 'Web Visits:',
                              id: 'deviatedLineItems.list.tooltip.webVisits'
                            }),
                            value: renderExactCount(item.visit, rowCurrency)
                          },
                          {
                            label: formatMessage({
                              defaultMessage: 'Web Visit Rate:',
                              id: 'deviatedLineItems.list.tooltip.webVisitRate'
                            }),
                            value: renderRate(item.webVisitRate, rowCurrency)
                          }
                        ),
                        <>
                          <span>{renderCount(item.visit, rowCurrency)}</span>
                          <span>{renderRate(item.webVisitRate, rowCurrency)}</span>
                        </>
                      )}
                    </td>
                  )}
                  {isVisible('footfall') && (
                    <td className="multiflightFootfall">
                      {renderMetricCell(
                        isEmptyMetric(item.footfallAttribution, item.storeVisitRate),
                        tooltipLines(
                          {
                            label: formatMessage({
                              defaultMessage: 'Store Visits:',
                              id: 'deviatedLineItems.list.tooltip.storeVisits'
                            }),
                            value: renderExactCount(item.footfallAttribution, rowCurrency)
                          },
                          {
                            label: formatMessage({
                              defaultMessage: 'Store Visit Rate:',
                              id: 'deviatedLineItems.list.tooltip.storeVisitRate'
                            }),
                            value: renderRate(item.storeVisitRate, rowCurrency)
                          }
                        ),
                        <>
                          <span>{renderCount(item.footfallAttribution, rowCurrency)}</span>
                          <span>{renderRate(item.storeVisitRate, rowCurrency)}</span>
                        </>
                      )}
                    </td>
                  )}
                  {isVisible('lastUpdate') && (
                    <td className="multiflightLastUpdate">
                      {item.lastUpdateTimeText ? (
                        <Tooltip
                          config={{
                            component: (
                              <span className="deviatedLineItemsList__tooltipBody">
                                {formatMessage(
                                  {
                                    defaultMessage: 'Local Time: {time}',
                                    id: 'deviatedLineItems.list.tooltip.lastUpdateTime'
                                  },
                                  { time: item.lastUpdateTimeText }
                                )}
                              </span>
                            ),
                            interactions: [TooltipInteractionEnum.hover],
                            placement: 'top',
                            portal: true,
                            strategy: 'fixed'
                          }}
                        >
                          <span>{item.lastUpdateText}</span>
                        </Tooltip>
                      ) : (
                        item.lastUpdateText
                      )}
                    </td>
                  )}
                  {isVisible('bidderRetries') && (
                    <td className="multiflightBidderRetries">
                      {item.bidderRetriesText === '-' ? (
                        item.bidderRetriesText
                      ) : (
                        <Tooltip
                          config={{
                            component: tooltipLines({
                              label: formatMessage({
                                defaultMessage: 'Sync Retries:',
                                id: 'deviatedLineItems.list.tooltip.bidderRetries'
                              }),
                              value: item.bidderRetriesText
                            }),
                            interactions: [TooltipInteractionEnum.hover],
                            placement: 'top',
                            portal: true,
                            strategy: 'fixed'
                          }}
                        >
                          <span>{item.bidderRetriesText}</span>
                        </Tooltip>
                      )}
                    </td>
                  )}
                  <td className="multiflightActions">
                    {actionsDivider}
                    {renderRowActions(item, rowCurrency)}
                  </td>
                </tr>
              );
            })}
            {isFetching && listItems.length > 0 && (
              <tr className="deviatedLineItemsList__loadingMoreRow">
                <td colSpan={visibleColumnCount}>
                  <div className="deviatedLineItemsList__loadingMore">
                    <i aria-hidden className="fa fa-spinner fa-spin" />
                    {formatMessage({ defaultMessage: 'Loading more...', id: 'taptapCoreList.loadingMore' })}
                  </div>
                </td>
              </tr>
            )}
            <tr aria-hidden className="deviatedLineItemsList__sentinel" ref={sentinelRef}>
              <td colSpan={visibleColumnCount} />
            </tr>
          </tbody>
        </table>
        {isFetching && listItems.length === 0 && (
          <div className="deviatedLineItemsList__loading">
            <BackgroundSpinner />
          </div>
        )}
        {isEmptyState && (
          <EmptyState
            className="deviatedLineItemsList__emptyState"
            description={formatMessage({
              defaultMessage: 'There is no data for the current search.',
              id: 'lineItems.list.emptyState.searchTitle'
            })}
            fontSizes={{ icon: '24px' }}
            icon="fa-solid fa-empty-set"
            show
            title={formatMessage({
              defaultMessage: 'No results found',
              id: 'deviatedLineItems.list.emptyState.title'
            })}
          />
        )}
      </div>
      {!!entityPropsPacingClicked && (
        <PacingStatusModal
          currencyInfo={modalCurrency ?? currencyInfo}
          entityId={entityPropsPacingClicked.lineItemId}
          entityType="lineItem"
          isOpen={!!entityPropsPacingClicked}
          onClose={handleCloseModal}
          userId={entityPropsPacingClicked.userId}
        />
      )}
      {forecastLineItem && (
        <ForecastModal
          baseCurrency={(modalCurrency ?? currencyInfo).baseCurrency}
          campaign={forecastLineItem}
          isModalOpened={!!forecastLineItem}
          onModalClose={handleForecastModalClose}
          userId={forecastLineItem.user}
        />
      )}
      {changelogConfig?.show && (
        <ChangelogModal
          entity="lineItem"
          entityId={changelogConfig.entityId}
          onCloseModalCallback={handleChangelogModalClose}
          title={changelogConfig.title}
          userId={changelogConfig.userId}
        />
      )}
      {activeDiagnosisLineItemId && diagnosisCache[activeDiagnosisLineItemId] && (
        <div className="deviatedLineItemsList__assistantChat">
          <AssistantChat
            buttonText={formatMessage({
              defaultMessage: 'Diagnostic',
              id: 'deviatedLineItems.list.actions.diagnostic'
            })}
            defaultAgentId="taptap-campaign-delivery-diagnostics"
            draggable
            id={`lineItemDiagnostics-${activeDiagnosisLineItemId}`}
            isOpen
            key={activeDiagnosisLineItemId}
            loadingData={false}
            onOpenChange={(open: boolean) => {
              if (!open) {
                setActiveDiagnosisLineItemId(null);
              }
            }}
            seedExchange={diagnosisCache[activeDiagnosisLineItemId]}
          />
        </div>
      )}
    </EntityListStyle>
  );
}
